import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Observable, of, Subscription } from 'rxjs';
import {
  BusinessCompanyThingValue,
  getBusinessCompanyThingValueLabel,
} from 'src/app/_enums/business/business-company-thing-value.enum';
import {
  BusinessInsuredObject,
  getBusinessInsuredObjectLabel,
} from 'src/app/_enums/business/business-insured-object.enum';
import {
  BusinessObjectsCover,
  getBusinessObjectsCoverLabel,
} from 'src/app/_enums/business/business-object-cover.enum';
import { InsuranceType } from 'src/app/_enums/insurance-type.enum';
import { getControl } from 'src/app/_helpers/utils';
import { BusinessThingsMetadata } from 'src/app/_types/lead.types';

import { LeadFormBase } from '../../lead-form-base';
import { SharedBusinessFormComponent } from '../shared-business-form/shared-business-form.component';

type BusinessCompanyThingsBasicCoverageFormGroup = {
  coverage: FormControl<boolean | null>;
  insuredSum: FormControl<number | null>;
  deductible: FormControl<number | null>;
  value: FormControl<BusinessCompanyThingValue | null>;
};

type FireDamageCoverageFormGroup = {
  fireNaturalDisasterCoverage: FormControl<boolean | null>;
  dicDilCoverage: FormControl<boolean | null>;
  insuredSum: FormControl<number | null>;
  value: FormControl<BusinessCompanyThingValue | null>;
  fireDeductible: FormControl<number | null>;
  specialFees: FormControl<number | null>;
  contingencyInsurance: FormControl<number | null>;
  externalInsurance: FormControl<number | null>;
  constructionHutTools: FormControl<number | null>;
  internalDisturbances: FormControl<number | null>;
  internalDistrubancesDeductible: FormControl<number | null>;
  monetaryValues: FormControl<number | null>;
  monetaryValuesInSafe: FormControl<number | null>;
};

type TheftCoverageFormGroup = {
  theftCoverage: FormControl<boolean | null>;
  insuredSum: FormControl<number | null>;
  value: FormControl<BusinessCompanyThingValue | null>;
  deductible: FormControl<number | null>;
  specialFees: FormControl<number | null>;
  contingencyInsurance: FormControl<number | null>;
  externalInsurance: FormControl<number | null>;
  constructionHutTools: FormControl<number | null>;
  monetaryValues: FormControl<number | null>;
  monetaryValuesInSafe: FormControl<number | null>;
};

type WaterDamageCoverageFormGroup = {
  waterDamageCoverage: FormControl<boolean | null>;
  insuredSum: FormControl<number | null>;
  value: FormControl<BusinessCompanyThingValue | null>;
  deductible: FormControl<number | null>;
  specialFees: FormControl<number | null>;
  contingencyInsurance: FormControl<number | null>;
  externalInsurance: FormControl<number | null>;
  constructionHutTools: FormControl<number | null>;
};

type EpidemicCoverageFormGroup = {
  pandemicIncluded: FormControl<boolean | null>;
  insuredSum: FormControl<number | null>;
  deductible: FormControl<number | null>;
  duration: FormControl<string | null>;
};

type TechnicalCoverageFormGroup = {
  technicalCoverage: FormControl<boolean | null>;
  insuredSum: FormControl<number | null>;
  deductible: FormControl<number | null>;
  insuranceType: FormControl<string | null>;
};

type LossCoverageFormGroup = {
  lossCoverage: FormControl<boolean | null>;
  fireNaturalDisasterCoverage: FormControl<boolean | null>;
  fireInsuredSum: FormControl<number | null>;
  fireDeductible: FormControl<number | null>;
  theftCoverage: FormControl<boolean | null>;
  theftInsuredSum: FormControl<number | null>;
  theftDeductible: FormControl<number | null>;
  waterDamageCoverage: FormControl<boolean | null>;
  waterInsuredSum: FormControl<number | null>;
  waterDeductible: FormControl<number | null>;
  revenue: FormControl<number | null>;
  grossInsuranceProfit: FormControl<number | null>;
};

@Component({
  selector: 'app-business-things-form',
  templateUrl: './business-things-form.component.html',
  styleUrls: ['./business-things-form.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatFormFieldModule,
    MatSelectModule,
    SharedBusinessFormComponent,
  ],
})
export class BusinessThingsFormComponent
  extends LeadFormBase
  implements OnDestroy, OnInit
{
  @Input()
  formGroup!: FormGroup;

  private subscriptions: Subscription[] = [];

  constructor(private fb: FormBuilder) {
    super();
  }

  sharedBusinessFormGroup: FormGroup = this.fb.group({
    activityArea: [null],
    activityDescription: [null],
    hasLaborAgreement: [null],
    laborAgreement: [null],
    isInternational: [null],
    activityCountries: [null],
    hasSubcontractors: [null],
    collaboratorsCount: [null],
    yearlyAnnualRevenue: [null],
    yearlySalaries: [null],
  });

  createBasicCoverageFormGroup(): FormGroup<BusinessCompanyThingsBasicCoverageFormGroup> {
    return this.fb.group({
      coverage: [null],
      insuredSum: [null],
      deductible: [null],
      value: [null],
    });
  }

  fireDamageCoverageFormGroup: FormGroup<FireDamageCoverageFormGroup> =
    this.fb.group({
      fireNaturalDisasterCoverage: [null],
      dicDilCoverage: [null],
      insuredSum: [null],
      value: [null],
      fireDeductible: [null],
      specialFees: [null],
      contingencyInsurance: [null],
      externalInsurance: [null],
      constructionHutTools: [null],
      internalDisturbances: [null],
      internalDistrubancesDeductible: [null],
      monetaryValues: [null],
      monetaryValuesInSafe: [null],
    });

  theftCoverageFormGroup: FormGroup<TheftCoverageFormGroup> = this.fb.group({
    theftCoverage: [null],
    insuredSum: [null],
    value: [null],
    deductible: [null],
    specialFees: [null],
    contingencyInsurance: [null],
    externalInsurance: [null],
    constructionHutTools: [null],
    monetaryValues: [null],
    monetaryValuesInSafe: [null],
  });

  waterDamageCoverageFormGroup: FormGroup<WaterDamageCoverageFormGroup> =
    this.fb.group({
      waterDamageCoverage: [null],
      insuredSum: [null],
      value: [null],
      deductible: [null],
      specialFees: [null],
      contingencyInsurance: [null],
      externalInsurance: [null],
      constructionHutTools: [null],
    });

  epidemicCoverageFormGroup: FormGroup<EpidemicCoverageFormGroup> =
    this.fb.group({
      pandemicIncluded: [null],
      insuredSum: [null],
      deductible: [null],
      duration: [null],
    });

  technicalCoverageFormGroup: FormGroup<TechnicalCoverageFormGroup> =
    this.fb.group({
      technicalCoverage: [null],
      insuredSum: [null],
      deductible: [null],
      insuranceType: [null],
    });

  lossCoverageFormGroup: FormGroup<LossCoverageFormGroup> = this.fb.group({
    lossCoverage: [null],
    fireNaturalDisasterCoverage: [null],
    fireInsuredSum: [null],
    fireDeductible: [null],
    theftCoverage: [null],
    theftInsuredSum: [null],
    theftDeductible: [null],
    waterDamageCoverage: [null],
    waterInsuredSum: [null],
    waterDeductible: [null],
    revenue: [null],
    grossInsuranceProfit: [null],
  });

  metadataFormGroup: FormGroup = this.fb.group({
    type: InsuranceType.BUSINESS_COMPANY_THINGS,
    reimbursedSum: [null],
    insuredSum: [null],
    insuredObjects: [null],
    otherObject: [null],
    hasInsurance: [null],
    currentInsurer: [null],
    hasClaimsLastFiveYears: [null],
    fireDamageCoverage: this.fireDamageCoverageFormGroup,
    earthquakeCoverage: this.createBasicCoverageFormGroup(),
    terrorismCoverage: this.createBasicCoverageFormGroup(),
    theftCoverage: this.theftCoverageFormGroup,
    breakageCoverage: this.createBasicCoverageFormGroup(),
    waterDamageCoverage: this.waterDamageCoverageFormGroup,
    epidemicCoverage: this.epidemicCoverageFormGroup,
    technicalCoverage: this.technicalCoverageFormGroup,
    lossCoverage: this.lossCoverageFormGroup,
    sharedBusiness: this.sharedBusinessFormGroup,
  });

  override ngOnInit(): void {
    super.ngOnInit();
    this.formGroup.removeControl('metadata');
    if (this.metadata) {
      const businessMetadata = this.metadata as BusinessThingsMetadata;

      // Patch shared business data
      if (businessMetadata.activityArea !== undefined) {
        this.sharedBusinessFormGroup.patchValue({
          activityArea: businessMetadata.activityArea,
          activityDescription: businessMetadata.activityDescription,
          hasLaborAgreement: businessMetadata.hasLaborAgreement,
          laborAgreement: businessMetadata.laborAgreement,
          isInternational: businessMetadata.isInternational,
          activityCountries: businessMetadata.activityCountries,
          hasSubcontractors: businessMetadata.hasSubcontractors,
          collaboratorsCount: businessMetadata.collaboratorsCount,
          yearlyAnnualRevenue: businessMetadata.yearlyAnnualRevenue,
          yearlySalaries: businessMetadata.yearlySalaries,
        });
      }

      // Patch coverage groups
      if (businessMetadata.fireDamageCoverage) {
        this.fireDamageCoverageFormGroup.patchValue(
          businessMetadata.fireDamageCoverage
        );
      }

      if (businessMetadata.earthquakeCoverage) {
        (
          this.metadataFormGroup.controls['earthquakeCoverage'] as FormGroup
        ).patchValue(businessMetadata.earthquakeCoverage);
      }

      if (businessMetadata.terrorismCoverage) {
        (
          this.metadataFormGroup.controls['terrorismCoverage'] as FormGroup
        ).patchValue(businessMetadata.terrorismCoverage);
      }

      if (businessMetadata.theftCoverage) {
        this.theftCoverageFormGroup.patchValue(businessMetadata.theftCoverage);
      }

      if (businessMetadata.breakageCoverage) {
        (
          this.metadataFormGroup.controls['breakageCoverage'] as FormGroup
        ).patchValue(businessMetadata.breakageCoverage);
      }

      if (businessMetadata.waterDamageCoverage) {
        this.waterDamageCoverageFormGroup.patchValue(
          businessMetadata.waterDamageCoverage
        );
      }

      if (businessMetadata.epidemicCoverage) {
        this.epidemicCoverageFormGroup.patchValue(
          businessMetadata.epidemicCoverage
        );
      }

      if (businessMetadata.technicalCoverage) {
        this.technicalCoverageFormGroup.patchValue(
          businessMetadata.technicalCoverage
        );
      }

      if (businessMetadata.lossCoverage) {
        this.lossCoverageFormGroup.patchValue(businessMetadata.lossCoverage);
      }

      // Patch remaining top-level fields
      this.metadataFormGroup.patchValue({
        type: businessMetadata.type,
        reimbursedSum: businessMetadata.reimbursedSum,
        insuredSum: businessMetadata.insuredSum,
        insuredObjects: businessMetadata.insuredObjects,
        otherObject: businessMetadata.otherObject,
        hasInsurance: businessMetadata.hasInsurance,
        currentInsurer: businessMetadata.currentInsurer,
        hasClaimsLastFiveYears: businessMetadata.hasClaimsLastFiveYears,
      });
    }
    this.formGroup.addControl('metadata', this.metadataFormGroup);

    // Handle conditional fields
    this.setupConditionalFieldSubscriptions();
  }

  private setupConditionalFieldSubscriptions(): void {
    // Handle hasInsurance -> currentInsurer
    const hasInsuranceSub = this.metadataFormGroup.controls[
      'hasInsurance'
    ].valueChanges.subscribe((value: boolean | string | null) => {
      if (value !== true && value !== 'true') {
        this.metadataFormGroup.controls['currentInsurer'].reset();
      }
    });
    this.subscriptions.push(hasInsuranceSub);

    // Handle fireDamageCoverage.fireNaturalDisasterCoverage -> fireDamageCoverage fields
    const fireNaturalDisasterSub =
      this.fireDamageCoverageFormGroup.controls.fireNaturalDisasterCoverage.valueChanges.subscribe(
        (value: boolean | string | null) => {
          if (value !== true && value !== 'true') {
            const fireGroup = this.fireDamageCoverageFormGroup;
            fireGroup.controls.dicDilCoverage.reset();
            fireGroup.controls.insuredSum.reset();
            fireGroup.controls.value.reset();
            fireGroup.controls.fireDeductible.reset();
            fireGroup.controls.specialFees.reset();
            fireGroup.controls.contingencyInsurance.reset();
            fireGroup.controls.externalInsurance.reset();
            fireGroup.controls.constructionHutTools.reset();
            fireGroup.controls.internalDisturbances.reset();
            fireGroup.controls.internalDistrubancesDeductible.reset();
            fireGroup.controls.monetaryValues.reset();
            fireGroup.controls.monetaryValuesInSafe.reset();
          }
        }
      );
    this.subscriptions.push(fireNaturalDisasterSub);

    // Handle basic coverage groups (earthquake, terrorism, breakage)
    const basicCoverageGroups = [
      'earthquakeCoverage',
      'terrorismCoverage',
      'breakageCoverage',
    ] as const;
    basicCoverageGroups.forEach((groupName) => {
      const coverageGroup = this.metadataFormGroup.controls[
        groupName
      ] as FormGroup;
      const coverageControl = coverageGroup.controls['coverage'];

      const coverageSub = coverageControl.valueChanges.subscribe(
        (value: boolean | string | null) => {
          if (value !== true && value !== 'true') {
            coverageGroup.controls['insuredSum'].reset();
            coverageGroup.controls['deductible'].reset();
            coverageGroup.controls['value'].reset();
          }
        }
      );
      this.subscriptions.push(coverageSub);
    });

    // Handle theftCoverage.theftCoverage
    const theftCoverageSub =
      this.theftCoverageFormGroup.controls.theftCoverage.valueChanges.subscribe(
        (value: boolean | string | null) => {
          if (value !== true && value !== 'true') {
            const theftGroup = this.theftCoverageFormGroup;
            theftGroup.controls.insuredSum.reset();
            theftGroup.controls.value.reset();
            theftGroup.controls.deductible.reset();
            theftGroup.controls.specialFees.reset();
            theftGroup.controls.contingencyInsurance.reset();
            theftGroup.controls.externalInsurance.reset();
            theftGroup.controls.constructionHutTools.reset();
            theftGroup.controls.monetaryValues.reset();
            theftGroup.controls.monetaryValuesInSafe.reset();
          }
        }
      );
    this.subscriptions.push(theftCoverageSub);

    // Handle waterDamageCoverage.waterDamageCoverage
    const waterDamageCoverageSub =
      this.waterDamageCoverageFormGroup.controls.waterDamageCoverage.valueChanges.subscribe(
        (value: boolean | string | null) => {
          if (value !== true && value !== 'true') {
            const waterGroup = this.waterDamageCoverageFormGroup;
            waterGroup.controls.insuredSum.reset();
            waterGroup.controls.value.reset();
            waterGroup.controls.deductible.reset();
            waterGroup.controls.specialFees.reset();
            waterGroup.controls.contingencyInsurance.reset();
            waterGroup.controls.externalInsurance.reset();
            waterGroup.controls.constructionHutTools.reset();
          }
        }
      );
    this.subscriptions.push(waterDamageCoverageSub);

    // Handle epidemicCoverage.pandemicIncluded
    const pandemicIncludedSub =
      this.epidemicCoverageFormGroup.controls.pandemicIncluded.valueChanges.subscribe(
        (value: boolean | string | null) => {
          if (value !== true && value !== 'true') {
            const epidemicGroup = this.epidemicCoverageFormGroup;
            epidemicGroup.controls.insuredSum.reset();
            epidemicGroup.controls.deductible.reset();
            epidemicGroup.controls.duration.reset();
          }
        }
      );
    this.subscriptions.push(pandemicIncludedSub);

    // Handle technicalCoverage.technicalCoverage
    const technicalCoverageSub =
      this.technicalCoverageFormGroup.controls.technicalCoverage.valueChanges.subscribe(
        (value: boolean | string | null) => {
          if (value !== true && value !== 'true') {
            const technicalGroup = this.technicalCoverageFormGroup;
            technicalGroup.controls.insuredSum.reset();
            technicalGroup.controls.deductible.reset();
            technicalGroup.controls.insuranceType.reset();
          }
        }
      );
    this.subscriptions.push(technicalCoverageSub);

    // Handle lossCoverage.lossCoverage
    const lossCoverageSub =
      this.lossCoverageFormGroup.controls.lossCoverage.valueChanges.subscribe(
        (value: boolean | string | null) => {
          if (value !== true && value !== 'true') {
            const lossGroup = this.lossCoverageFormGroup;
            lossGroup.controls.fireNaturalDisasterCoverage.reset();
            lossGroup.controls.fireInsuredSum.reset();
            lossGroup.controls.fireDeductible.reset();
            lossGroup.controls.theftCoverage.reset();
            lossGroup.controls.theftInsuredSum.reset();
            lossGroup.controls.theftDeductible.reset();
            lossGroup.controls.waterDamageCoverage.reset();
            lossGroup.controls.waterInsuredSum.reset();
            lossGroup.controls.waterDeductible.reset();
            lossGroup.controls.revenue.reset();
            lossGroup.controls.grossInsuranceProfit.reset();
          }
        }
      );
    this.subscriptions.push(lossCoverageSub);

    // Handle lossCoverage.fireNaturalDisasterCoverage -> fire fields
    const lossFireSub =
      this.lossCoverageFormGroup.controls.fireNaturalDisasterCoverage.valueChanges.subscribe(
        (value: boolean | string | null) => {
          if (value !== true && value !== 'true') {
            this.lossCoverageFormGroup.controls.fireInsuredSum.reset();
            this.lossCoverageFormGroup.controls.fireDeductible.reset();
          }
        }
      );
    this.subscriptions.push(lossFireSub);

    // Handle lossCoverage.theftCoverage -> theft fields
    const lossTheftSub =
      this.lossCoverageFormGroup.controls.theftCoverage.valueChanges.subscribe(
        (value: boolean | string | null) => {
          if (value !== true && value !== 'true') {
            this.lossCoverageFormGroup.controls.theftInsuredSum.reset();
            this.lossCoverageFormGroup.controls.theftDeductible.reset();
          }
        }
      );
    this.subscriptions.push(lossTheftSub);

    // Handle lossCoverage.waterDamageCoverage -> water fields
    const lossWaterSub =
      this.lossCoverageFormGroup.controls.waterDamageCoverage.valueChanges.subscribe(
        (value: boolean | string | null) => {
          if (value !== true && value !== 'true') {
            this.lossCoverageFormGroup.controls.waterInsuredSum.reset();
            this.lossCoverageFormGroup.controls.waterDeductible.reset();
          }
        }
      );
    this.subscriptions.push(lossWaterSub);

    // Handle insuredObjects -> otherObject
    const insuredObjectsSub = this.metadataFormGroup.controls[
      'insuredObjects'
    ].valueChanges.subscribe((value: BusinessInsuredObject[] | null) => {
      if (
        !value ||
        !Array.isArray(value) ||
        !value.includes(BusinessInsuredObject.OTHER)
      ) {
        this.metadataFormGroup.controls['otherObject'].reset();
      }
    });
    this.subscriptions.push(insuredObjectsSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      if (sub) {
        sub.unsubscribe();
      }
    });
    this.subscriptions = [];
  }

  // Enum values and labels
  insuredObjectValues = Object.values(BusinessInsuredObject);

  getBusinessInsuredObjectLabel = getBusinessInsuredObjectLabel;

  coverValues = Object.values(BusinessObjectsCover);

  getBusinessObjectsCoverLabel = getBusinessObjectsCoverLabel;

  thingValueValues = Object.values(BusinessCompanyThingValue);

  getBusinessCompanyThingValueLabel = getBusinessCompanyThingValueLabel;

  getControl(control: AbstractControl): FormControl {
    return getControl(control);
  }

  hasOtherSelected(): boolean {
    const insuredObjects =
      this.metadataFormGroup.controls['insuredObjects'].value;
    return (
      Array.isArray(insuredObjects) &&
      insuredObjects.includes(BusinessInsuredObject.OTHER)
    );
  }

  override handleSubmit(): Observable<boolean> {
    // Merge shared business form data into the main metadata form
    const sharedData = this.sharedBusinessFormGroup.value;
    Object.keys(sharedData).forEach((key) => {
      this.metadataFormGroup.controls[key] =
        this.sharedBusinessFormGroup.controls[key];
    });
    this.metadataFormGroup.removeControl('sharedBusiness');
    return of(true);
  }
}
